import React, { useRef, useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Image,
  ImageSourcePropType,
  useWindowDimensions,
  Platform,
  Share as RNShare,
} from 'react-native';
import Share from 'react-native-share';
import { ArrowLeft } from 'lucide-react-native';
import { Text } from '@/components';
import { captureRef } from 'react-native-view-shot';
import * as Haptics from 'expo-haptics';
import * as ImagePicker from 'expo-image-picker';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TrailSharePreview, { ShareFormat } from '@/components/TrailSharePreview';
import { useTrailDetail } from '@/lib/queries/trails';
import { useAuth } from '@/context/AuthContext';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { formatDuration, formatPace } from '@/utils/distance';
import { loadEndWalkDraft } from '@/utils/endWalkDraft';
import { useTranslation } from 'react-i18next';
import LottieLoader from '@/components/LottieLoader';
import { logEvent } from '@/lib/analytics';

/** Fake PNG background used in the preview when no custom photo is chosen.
 *  When exporting, this is hidden so the PNG has a transparent background. */
const FAKE_PNG_BG: ImageSourcePropType = require('../../assets/images/share-icons/FakePNGBackground.png');

/** Share screen for a saved trail (`?trailId=`) or an end-walk draft (`?draftId=`). */
export default function EndWalkShareScreen() {
  const { t } = useTranslation();
  const { draftId, trailId } = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { session } = useAuth();

  const [draft, setDraft] = React.useState<any>(null);
  const [draftLoading, setDraftLoading] = React.useState(!!draftId);
  const [gradientStyle, setGradientStyle] = useState<ShareFormat>('share_1');
  const [shareImageUri, setShareImageUri] = useState<string | null>(null);
  const [capturingForExport, setCapturingForExport] = useState(false);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isSharingLink, setIsSharingLink] = useState(false);
  // Stable per-page refs — never hop between views, so the captured tag
  // always points to a mounted view
  const pageRefs = useRef<(View | null)[]>([]);

  // Saved-trail source (when ?trailId= is passed, e.g. from the detail screen)
  const trailQuery = useTrailDetail(
    typeof trailId === 'string' ? trailId : undefined,
    session?.accessToken,
  );

  const gradientStyles: ShareFormat[] = ['share_1', 'share_2', 'share_3', 'share_4', 'share_5', 'share_6'];

  // Scaled preview: keep the 9:16 aspect, fit the space between the header
  // and the sticky footer. The card renders at its design size (312×554.7)
  // and is transform-scaled down for display, so fonts/icons scale with it.
  // Capture targets the card itself with scale snapped to 1, so the exported
  // PNG is always design-size × device density regardless of display scale.
  // ponytail: chrome estimate is a fixed constant — tune if the header/footer
  // heights change.
  const { width: winW, height: winH } = useWindowDimensions();
  const NATURAL_W = 312;
  const NATURAL_H = (NATURAL_W * 16) / 9;
  const maxPreviewH = winH - insets.top - insets.bottom - 300;
  // No 320 floor — flooring bigger than what fits is what pushed the card
  // behind the footer on small screens. 160 only guards degenerate values.
  const previewH = Math.max(160, Math.min(NATURAL_H, maxPreviewH));
  const previewW = (previewH * 9) / 16;
  const previewScale = previewW / NATURAL_W;
  const sharePageWidth = winW;
  const sharePageStride = sharePageWidth;

  React.useEffect(() => {
    (async () => {
      if (trailId) return; // trail comes from the API query
      if (typeof draftId !== 'string') {
        setDraftLoading(false);
        return;
      }
      setDraftLoading(true);
      try {
        const parsed = await loadEndWalkDraft(draftId);
        setDraft(parsed ?? null);
      } catch (e) {
        console.error('Error loading draft for share:', e);
      } finally {
        setDraftLoading(false);
      }
    })();
  }, [draftId, trailId]);

  const trail = trailId ? trailQuery.data : draft;
  const isLoading = trailId ? trailQuery.isLoading : draftLoading;

  const handlePickImage = async () => {
    try {
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permissionResult.granted) {
        Alert.alert(t('trailDetail.permissionRequired'), t('trailDetail.photoLibraryPermissionMessage'));
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 0.9,
        aspect: [3, 4],
        preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        setShareImageUri(result.assets[0].uri);
      }
    } catch (err) {
      console.error('Image picker error:', err);
    }
  };

  const handleRemoveImage = () => {
    setShareImageUri(null);
  };

  // Link-only share: recipients tap the trail URL and the app (or web app)
  // opens it. Image-free so the two share modes stay explicit.
  const handleShareTrailLink = async () => {
    if (!(typeof trailId === 'string' && trailId) || !trail) return;
    Haptics.selectionAsync();
    setIsSharingLink(true);
    try {
      if (Platform.OS === 'web') {
        // Web: react-native-share shim → navigator.share({url}) / clipboard.
        await Share.open({
          url: `https://app.tailtrails.club/trail/${trailId}`,
          title: trail.name || t('trailShare.shareTitle'),
        });
      } else {
        await Share.open({
          title: trail.name || t('trailShare.shareTitle'),
          message: t('trailShare.shareMessage', {
            trail: trail.name || t('trailShare.thisTrail'),
            link: `https://app.tailtrails.club/trail/${trailId}`,
          }),
          failOnCancel: false,
        });
      }
      logEvent('share', { content_type: 'trail_link', item_id: String(trailId) });
    } catch (error: any) {
      // react-native-share rejects with a message on user-cancel — not an error.
      if (String(error?.message || '').toLowerCase().includes('cancel')) return;
      console.error('Share link error:', error);
    } finally {
      setIsSharingLink(false);
    }
  };

  const handleShare = async () => {
    const activeIndex = gradientStyles.indexOf(gradientStyle);
    const target = pageRefs.current[activeIndex];
    if (!trail || !target) return;

    Haptics.selectionAsync();
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setIsCreatingShare(true);

    try {
      // No custom background → capture in transparent mode (transparent PNG export).
      const noCustomBg = !shareImageUri && !trail.photo;
      if (noCustomBg) {
        setCapturingForExport(true);
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      }

      // Retry: a just-swiped page's native view may not be registered yet
      let uri: string | undefined;
      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          uri = await captureRef(target, {
            format: 'png',
            quality: 0.95,
          });
          break;
        } catch (err) {
          lastError = err;
          await new Promise(r => setTimeout(r, 150));
        }
      }
      if (!uri) throw lastError;

      if (noCustomBg) {
        setCapturingForExport(false);
      }

      // Shared link is an https universal/App Link (claimed domain in
      // app.json via associatedDomains / intentFilters autoVerify), so on
      // devices with the app it opens app/trail/[id].tsx directly, and
      // without the app it lands on the web version of the app for that
      // same route (login required by the API, then it renders). Verified
      // via the .well-known files the web build serves at the same domain.
      const deepLink = typeof trailId === 'string' && trailId
        ? `https://app.tailtrails.club/trail/${trailId}`
        : undefined;
      const shareMessage = deepLink
        ? t('trailShare.shareMessage', {
            trail: trail.name || t('trailShare.thisTrail'),
            link: deepLink,
          })
        : trail.name || ' ';

      // react-native-share: RN's built-in Share.share ignores `url` on
      // Android (text/plain only — see ShareModule.kt), so the image never
      // attached there. This packages the file + caption properly.
      await Share.open({
        url: uri,
        message: shareMessage,
        title: trail.name || t('trailShare.shareTitle'),
        type: 'image/png',
        failOnCancel: false,
      });
      logEvent('share', { content_type: 'trail', item_id: String(trail.id) });
      router.back();
    } catch (error: any) {
      console.error('Share error:', error);
      Alert.alert(t('common.error'), error?.message || t('trailDetail.failedToShare'));
    } finally {
      setIsCreatingShare(false);
    }
  };

  const handleBack = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.back();
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <LottieLoader size={120} />
      </View>
    );
  }

  if (!trail) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>{t('trailDetail.trailNotFound')}</Text>
        <TouchableOpacity onPress={handleBack} style={styles.backLink}>
          <Text style={styles.backLinkText}>{t('common.goBack')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <Stack.Screen options={{ headerShown: false }} />
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
          <ArrowLeft size={24} color={theme.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('trailDetail.shareTrail')}</Text>
        <View style={{ width: 44 }} />
      </View>

      <View style={styles.content}>
        {/* Add photo — solid dark box per the design */}
        <View style={styles.section}>
          {shareImageUri ? (
            <View style={styles.imagePreviewRow}>
              <Image source={{ uri: shareImageUri }} style={styles.imageThumbnail} />
              <TouchableOpacity onPress={handleRemoveImage} style={styles.imageRemoveButton}>
                <Text style={styles.imageRemoveText}>{t('common.remove')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={styles.imagePickerButton} onPress={handlePickImage}>
              <Text style={styles.imagePickerText}>{t('trailDetail.addPhoto')}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Preview pager — swipe to change style (dots below) */}
        <View style={styles.previewPagerWrap}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            snapToInterval={sharePageStride}
            snapToAlignment="start"
            decelerationRate="fast"
            contentContainerStyle={styles.pagerContent}
            onScroll={(e) => {
              const i = Math.round(e.nativeEvent.contentOffset.x / sharePageStride);
              setGradientStyle(gradientStyles[Math.min(i, gradientStyles.length - 1)] ?? gradientStyles[0]);
            }}
            scrollEventThrottle={16}
          >
            {gradientStyles.map((styleOption, i) => (
              <View key={styleOption} style={[styles.previewPage, { width: sharePageWidth, alignItems: 'center', justifyContent: 'center' }]}>
                {/* Wrapper is the display box; the card renders at natural
                    design size and is transform-scaled into it, so fonts/icons
                    scale with it. The card itself is the capture target and
                    briefly renders at scale 1 while capturing, so the export
                    is always full design resolution. */}
                <View style={{ width: previewW, height: previewH, alignItems: 'center', justifyContent: 'center' }}>
                  <View
                    ref={(v) => { pageRefs.current[i] = v; }}
                    collapsable={false}
                    style={{ width: NATURAL_W, height: NATURAL_H, transform: [{ scale: capturingForExport && gradientStyle === styleOption ? 1 : previewScale }] }}
                  >
                    <TrailSharePreview
                      format={styleOption}
                      backgroundImage={shareImageUri ? { uri: shareImageUri } : (trail.photo ? { uri: trail.photo } : FAKE_PNG_BG)}
                      transparentBackground={capturingForExport}
                      coordinates={trail.coordinates}
                      path={trail.path}
                      distance={{
                        value: `${(trail.distance / 1000).toFixed(2)}`,
                        label: 'DISTANCE',
                        unit: 'KM',
                      }}
                      duration={{
                        value: formatDuration(trail.duration),
                        label: 'DURATION',
                        unit: 'TIME',
                      }}
                      pace={{
                        value: formatPace(trail.duration, trail.distance),
                        label: 'PACE',
                        unit: 'MIN/KM',
                      }}
                      style={{ width: NATURAL_W, height: NATURAL_H }}
                    />
                  </View>
                </View>
              </View>
            ))}
          </ScrollView>
        </View>
      </View>

      {/* Action Button */}
      <View style={[styles.footer, { paddingBottom: Math.max(16, insets.bottom + 8) }]}>
        {/* Style pager dots, in the sticky footer above the share button */}
        <View style={styles.dotsRow} pointerEvents="none">
          {gradientStyles.map((styleOption, i) => (
            <View
              key={styleOption}
              style={[styles.dot, gradientStyle === styleOption && styles.dotActive]}
            />
          ))}
        </View>
        {typeof trailId === 'string' && trailId ? (
          <View style={styles.footerRow}>
            <TouchableOpacity
              style={[styles.shareButton, styles.shareButtonSecondary, isSharingLink && styles.shareButtonDisabled]}
              onPress={handleShareTrailLink}
              disabled={isSharingLink || isCreatingShare}
            >
              <Text style={styles.shareButtonTextSecondary}>{t('trailDetail.shareTrail')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.shareButton, styles.shareButtonFlex, isCreatingShare && styles.shareButtonDisabled]}
              onPress={handleShare}
              disabled={isCreatingShare || isSharingLink}
            >
              {isCreatingShare ? (
                <View style={styles.loadingSpinner} />
              ) : (
                <Text style={styles.shareButtonText}>{t('trailDetail.shareImage')}</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.shareButton, isCreatingShare && styles.shareButtonDisabled]}
            onPress={handleShare}
            disabled={isCreatingShare}
          >
            {isCreatingShare ? (
              <View style={styles.loadingSpinner} />
            ) : (
              <Text style={styles.shareButtonText}>{t('trailDetail.shareImage')}</Text>
            )}
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    ...Typography.body(theme.textMuted),
    marginBottom: 12,
  },
  backLink: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: theme.backgroundSecondary,
  },
  backLinkText: {
    ...Typography.body(theme.accentPrimary),
    fontWeight: '600',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    ...Typography.h1(theme.textPrimary),
    fontSize: 18,
  },
  content: {
    flex: 1,
    paddingHorizontal: 14,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    ...Typography.body(theme.textPrimary),
    fontWeight: '600',
    marginBottom: 12,
  },
  imagePreviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  imageThumbnail: {
    width: 56,
    height: 56,
    borderRadius: 10,
    backgroundColor: theme.borderSubtle,
  },
  imageRemoveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: theme.backgroundSecondary,
  },
  imageRemoveText: {
    ...Typography.caption(theme.textPrimary),
  },
  imagePickerButton: {
    height: 55,
    borderRadius: 14,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 0.5,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePickerText: {
    ...Typography.body(theme.textMuted),
    fontWeight: '500',
  },
  previewPagerWrap: {
    // bleed to the screen edges: cards run flush left/right past the 14px content inset
    marginHorizontal: -14,
  },
  pagerContent: {
    // pages tile edge-to-edge, flush with the screen edges — no slivers
  },
  previewPage: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 5,
    marginBottom: 12,
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: theme.backgroundSecondaryVarient,
  },
  dotActive: {
    backgroundColor: theme.accentPrimary,
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 12,
    backgroundColor: theme.backgroundPrimary,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.borderSubtle,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 10,
  },
  shareButtonFlex: {
    flex: 1,
  },
  shareButtonSecondary: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.accentPrimary,
  },
  shareButtonTextSecondary: {
    ...Typography.h2(theme.accentPrimary),
    fontSize: 16,
    fontWeight: '600',
  },
  shareButton: {
    backgroundColor: theme.accentPrimary,
    paddingVertical: 16,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareButtonDisabled: {
    opacity: 0.6,
  },
  shareButtonText: {
    ...Typography.h2(theme.backgroundPrimary),
    fontSize: 16,
    fontWeight: '600',
  },
  loadingSpinner: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.3)',
    borderTopColor: theme.backgroundPrimary,
  },
});